// src/pages/affiliate/PayoutsPage.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useUI } from '../../context/UIContext';
import { useAffiliate } from '../../context/AffiliateContext';
import { isPendingApprovalError } from '../../context/AffiliateContext';
import { getBaseUrl } from '../../config/api';
import PendingApprovalView from '../../components/affiliate/PendingApprovalView';
import '../../styles/AffiliatePayouts.css';

interface Payout {
  _id: string;
  payoutId: string;
  amount: number; // Backend uses 'amount', not 'totalAmount'
  commissionCount: number;
  method: string;
  payoutDetails: {
    paypalEmail?: string;
    recipientEmail?: string;
    bankAccountNumber?: string;
    recipientAccount?: string;
    mobileMoneyNumber?: string;
    phoneNumber?: string;
    transactionId?: string;
    processingFee?: number;
    netAmount?: number;
  };
  status: string;
  processedBy?: string;
  createdAt: string;
  requestedAt?: string;
  processedAt?: string;
}

const AffiliatePayoutsPage: React.FC = () => {
  const { showSnackbar } = useUI();
  const { isPending, setAffiliateStatus } = useAffiliate();
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [availableBalance, setAvailableBalance] = useState(0);
  const [minimumPayout, setMinimumPayout] = useState(50);
  const [paypalEmail, setPaypalEmail] = useState('');
  const [methodSetup, setMethodSetup] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showRequestModal, setShowRequestModal] = useState(false);
  
  // Payout request form
  const [payoutAmount, setPayoutAmount] = useState('');

  const handleAuthError = (status: number, message?: string) => {
    if (status === 401) {
      localStorage.removeItem('affiliateToken');
      sessionStorage.removeItem('affiliateToken');
      localStorage.removeItem('affiliateUser');
      sessionStorage.removeItem('affiliateUser');
      setAffiliateStatus(null);
      showSnackbar('Session expired. Please log in again.', 'error');
      window.location.href = '/affiliate/login';
      return true;
    }
    if (status === 403 && isPendingApprovalError(status, message)) {
      setAffiliateStatus('pending');
      showSnackbar('Your account is pending approval. Payouts will be available once approved.', 'info');
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (isPending) return;
    fetchPayouts();
    fetchAvailableBalance();
  }, [isPending]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPayouts = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('affiliateToken') || sessionStorage.getItem('affiliateToken');
      
      if (!token) {
        showSnackbar('Please log in to view payouts', 'error');
        return;
      }

      const response = await fetch(
        `${getBaseUrl()}/v1/affiliate/payouts`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        // Backend returns data.data.payouts (array) or data.data (array)
        const payoutsData = data.data?.payouts || (Array.isArray(data.data) ? data.data : []);
        setPayouts(payoutsData);
      } else {
        if (handleAuthError(response.status, data.message)) return;
        showSnackbar(data.message || 'Failed to load payouts', 'error');
      }
    } catch (error) {
      console.error('Fetch payouts error:', error);
      showSnackbar('An error occurred loading payouts', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAvailableBalance = async () => {
    try {
      const token = localStorage.getItem('affiliateToken') || sessionStorage.getItem('affiliateToken');
      if (!token) return;

      // Try to get balance from payout-info endpoint first (more accurate)
      const payoutInfoResponse = await fetch(
        `${getBaseUrl()}/v1/affiliate/payout-info`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (payoutInfoResponse.ok) {
        const payoutInfoData = await payoutInfoResponse.json();
        if (payoutInfoData.success) {
          setAvailableBalance(payoutInfoData.data.availableBalance || payoutInfoData.data.pendingEarnings || 0);
          if (typeof payoutInfoData.data.minimumPayout === 'number') {
            setMinimumPayout(payoutInfoData.data.minimumPayout);
          }
          setPaypalEmail(payoutInfoData.data.paypalEmail || '');
          setMethodSetup(Boolean(payoutInfoData.data.methodSetup));
          return;
        }
      }

      const payoutInfoData = await payoutInfoResponse.json().catch(() => ({}));
      if (payoutInfoResponse.status === 403 && isPendingApprovalError(403, payoutInfoData.message)) {
        setAffiliateStatus('pending');
        return;
      }

      // Fallback to dashboard stats (allowed for pending)
      const response = await fetch(
        `${getBaseUrl()}/v1/affiliate/dashboard/stats`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        setAvailableBalance(data.data.availableBalance || 0);
      }
    } catch (error) {
      console.error('Fetch balance error:', error);
    }
  };

  const handleRequestPayout = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!methodSetup) {
      showSnackbar('Add your PayPal email on the Profile page before requesting a payout', 'error');
      return;
    }

    const amount = parseFloat(payoutAmount);

    // Validation
    if (isNaN(amount) || amount <= 0) {
      showSnackbar('Please enter a valid amount', 'error');
      return;
    }

    if (amount < minimumPayout) {
      showSnackbar(`Minimum payout amount is ${formatCurrency(minimumPayout)}`, 'error');
      return;
    }

    if (amount > availableBalance) {
      showSnackbar('Insufficient balance', 'error');
      return;
    }

    try {
      const token = localStorage.getItem('affiliateToken') || sessionStorage.getItem('affiliateToken');

      const response = await fetch(
        `${getBaseUrl()}/v1/affiliate/request-payout`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            amount,
            method: 'paypal'
          })
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        showSnackbar('Payout request submitted successfully', 'success');
        setShowRequestModal(false);
        setPayoutAmount('');
        fetchPayouts();
        fetchAvailableBalance();
      } else {
        if (handleAuthError(response.status, data.message)) return;
        showSnackbar(data.message || data.error || 'Failed to submit payout request', 'error');
      }
    } catch (error) {
      console.error('Payout request error:', error);
      showSnackbar('An error occurred submitting your request', 'error');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'completed':
        return 'badge-success';
      case 'pending':
        return 'badge-warning';
      case 'processing':
        return 'badge-info';
      case 'failed':
        return 'badge-error';
      default:
        return 'badge-default';
    }
  };

  if (isPending) {
    return <PendingApprovalView title="Payouts" />;
  }

  return (
    <div className="affiliate-payouts">
      <div className="affiliate-payouts__header">
        <div>
          <h1>Payout Management</h1>
          <p>Request withdrawals and view payout history</p>
        </div>
        <button
          onClick={() => setShowRequestModal(true)}
          className="btn-primary"
          disabled={!methodSetup || availableBalance < minimumPayout}
        >
          <i className="fas fa-money-bill-wave"></i>
          Request Payout
        </button>
      </div>

      {/* Balance Card */}
      <div className="affiliate-payouts__balance-card">
        <div className="balance-card__content">
          <h2>Available Balance</h2>
          <p className="balance-card__amount">{formatCurrency(availableBalance)}</p>
          <small>
            Minimum payout: {formatCurrency(minimumPayout)}
            {methodSetup
              ? ` · PayPal: ${paypalEmail}`
              : ' · Add a PayPal email on your profile to request payouts'}
          </small>
        </div>
        <div className="balance-card__icon">
          <i className="fas fa-wallet"></i>
        </div>
      </div>

      {/* Payout History */}
      <div className="affiliate-payouts__history">
        <h2>Payout History</h2>

        {isLoading ? (
          <div className="affiliate-payouts__loading">
            <div className="spinner"></div>
            <p>Loading payouts...</p>
          </div>
        ) : payouts.length === 0 ? (
          <div className="affiliate-payouts__empty">
            <i className="fas fa-file-invoice-dollar"></i>
            <p>No payout history</p>
            <small>Your payout requests will appear here</small>
          </div>
        ) : (
          <div className="affiliate-payouts__table-container">
            <table className="affiliate-payouts__table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Method</th>
                  <th>Commissions</th>
                  <th>Status</th>
                  <th>Transaction ID</th>
                  <th>Processed Date</th>
                </tr>
              </thead>
              <tbody>
                {payouts.map((payout) => (
                  <tr key={payout._id}>
                    <td>{formatDate(payout.requestedAt || payout.createdAt)}</td>
                    <td className="amount">{formatCurrency(payout.amount)}</td>
                    <td>
                      <span className="method-badge">
                        {payout.method === 'paypal' && <i className="fab fa-paypal"></i>}
                        PayPal
                      </span>
                    </td>
                    <td>{payout.commissionCount}</td>
                    <td>
                      <span className={`badge ${getStatusBadgeClass(payout.status)}`}>
                        {payout.status}
                      </span>
                    </td>
                    <td>
                      {payout.payoutDetails?.transactionId ? (
                        <code title={payout.payoutDetails.transactionId}>
                          {payout.payoutDetails.transactionId.substring(0, 12)}...
                        </code>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>
                      {payout.processedAt ? formatDate(payout.processedAt) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Request Payout Modal */}
      {showRequestModal && (
        <div className="modal-overlay" onClick={() => setShowRequestModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Request Payout</h2>
              <button onClick={() => setShowRequestModal(false)} className="modal-close">
                <i className="fas fa-times"></i>
              </button>
            </div>

            <form onSubmit={handleRequestPayout} className="payout-form">
              <div className="form-group">
                <label>Available Balance</label>
                <div className="balance-display">{formatCurrency(availableBalance)}</div>
              </div>

              <div className="form-group">
                <label htmlFor="amount">Payout Amount *</label>
                <input
                  id="amount"
                  type="number"
                  step="0.01"
                  min={minimumPayout}
                  max={availableBalance}
                  value={payoutAmount}
                  onChange={(e) => setPayoutAmount(e.target.value)}
                  placeholder={`Min: ${formatCurrency(minimumPayout)}`}
                  required
                />
                <small>Enter amount between {formatCurrency(minimumPayout)} and {formatCurrency(availableBalance)}</small>
              </div>

              <div className="form-group">
                <div className="info-box" style={{
                  padding: '1rem',
                  background: '#edf2f7',
                  borderRadius: '8px',
                  marginBottom: '1rem'
                }}>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: '#4a5568', marginBottom: '0.5rem' }}>
                    <i className="fab fa-paypal" style={{ marginRight: '0.5rem', color: '#C9A84C' }}></i>
                    {methodSetup
                      ? `Payouts are sent to ${paypalEmail}.`
                      : 'Add your PayPal email on your profile before requesting a payout.'}
                  </p>
                  <Link 
                    to="/affiliate/profile" 
                    style={{ 
                      fontSize: '0.875rem', 
                      color: '#C9A84C', 
                      textDecoration: 'none',
                      fontWeight: 600
                    }}
                    onClick={() => setShowRequestModal(false)}
                  >
                    Update PayPal email →
                  </Link>
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => setShowRequestModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Submit Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AffiliatePayoutsPage;

